import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  aiGeneratePreview,
  campaignStep1,
  campaignStep2,
  campaignStep3,
  campaignStep4,
  fetchAvailableSlots,
  fetchBillboardCities,
  relaunchCampaign,
  saveAiCampaign,
  updateCampaignContent,
} from '@/features/campaigns/api/campaignApi';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';

function toastApiError(error: unknown, fallback: string) {
  toast.error(getErrorMessage(error, fallback));
}

type MutationToastOpts = {
  /** When true, skip react-toastify — Manual Builder uses alert dialogs instead. */
  silent?: boolean;
};

/** After launch / relaunch / AI save — refresh list, dashboard counts, wallet. */
function useInvalidateAfterCampaignWrite() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.root });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.root });
    void queryClient.invalidateQueries({ queryKey: queryKeys.wallet.root });
  };
}

export function useCampaignStep1(opts?: MutationToastOpts) {
  return useMutation({
    mutationFn: campaignStep1,
    onError: (error) => {
      if (opts?.silent) return;
      toastApiError(error, 'Failed to create campaign draft');
    },
  });
}

export function useCampaignStep2(opts?: MutationToastOpts) {
  return useMutation({
    mutationFn: campaignStep2,
    onSuccess: (data) => {
      if (opts?.silent) return;
      if (data?.message) toast.success(data.message);
    },
    onError: (error) => {
      if (opts?.silent) return;
      toastApiError(error, 'Failed to save screens');
    },
  });
}

export function useCampaignStep3(opts?: MutationToastOpts) {
  return useMutation({
    mutationFn: campaignStep3,
    onSuccess: (data) => {
      if (opts?.silent) return;
      if (data?.message) toast.success(data.message);
    },
    onError: (error) => {
      if (opts?.silent) return;
      toastApiError(error, 'Failed to book slots');
    },
  });
}

/** Re-runs step 3 on an existing booking to re-price it (coupon changes) — no "slots booked" toast. */
export function useCampaignReprice() {
  return useMutation({
    mutationFn: campaignStep3,
  });
}

export function useCampaignStep4(opts?: MutationToastOpts) {
  const invalidate = useInvalidateAfterCampaignWrite();
  return useMutation({
    mutationFn: campaignStep4,
    onSuccess: () => {
      invalidate();
    },
    onError: (error) => {
      if (opts?.silent) return;
      toastApiError(error, 'Failed to launch campaign');
    },
  });
}

export function useRelaunchCampaign() {
  const invalidate = useInvalidateAfterCampaignWrite();
  return useMutation({
    mutationFn: relaunchCampaign,
    onSuccess: () => {
      invalidate();
    },
    onError: (error) => toastApiError(error, 'Failed to relaunch campaign'),
  });
}

/** Re-runs relaunch with a coupon to re-price — no list invalidate / no default toast. */
export function useRelaunchReprice() {
  return useMutation({
    mutationFn: relaunchCampaign,
  });
}

export function useAvailableSlotsMutation(opts?: MutationToastOpts) {
  return useMutation({
    mutationFn: fetchAvailableSlots,
    onError: (error) => {
      if (opts?.silent) return;
      toastApiError(error, 'Failed to load slots');
    },
  });
}

export function useAiGeneratePreview() {
  return useMutation({
    mutationFn: aiGeneratePreview,
    onError: (error) => toastApiError(error, 'AI preview generation failed'),
  });
}

export function useSaveAiCampaign() {
  const invalidate = useInvalidateAfterCampaignWrite();
  return useMutation({
    mutationFn: saveAiCampaign,
    onSuccess: () => {
      invalidate();
    },
    onError: (error) => toastApiError(error, 'Failed to save AI campaign'),
  });
}

export function useUpdateCampaignContent() {
  const invalidate = useInvalidateAfterCampaignWrite();
  return useMutation({
    mutationFn: updateCampaignContent,
    onSuccess: (data) => {
      invalidate();
      if (data?.message) toast.success(data.message);
    },
    onError: (error) => toastApiError(error, 'Failed to update content'),
  });
}

export function useBillboardCitiesInfinite(search: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.campaigns.cities(search),
    queryFn: ({ pageParam }) => fetchBillboardCities(search, pageParam, 20),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const hasMore =
        lastPage?.pagination?.hasMore ??
        lastPage?.hasMore ??
        (lastPage?.data?.length ?? 0) >= 20;
      return hasMore ? lastPageParam + 1 : undefined;
    },
    enabled,
    refetchOnWindowFocus: false,
  });
}
